import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Badge } from '../../ui/badge';
import { Download, Printer, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { ledgerApi, apiError } from '../../../lib/api';
import { acct, DateField, downloadCSV, Empty, Line, money, PageTitle, today, yearStart } from './shared';

const monthName = (k) => new Date(`${k}-01T00:00:00`).toLocaleString('en-US', { month: 'short', year: '2-digit' });

function useReport(load, deps) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const run = useCallback(() => {
    setLoading(true);
    load().then(setData).catch((e) => toast.error(apiError(e, 'Could not load the report'))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(() => { run(); }, [run]);
  return [data, loading, run];
}

const Header = ({ title, sub, children }) => (
  <div className="p-5 bg-slate-900 text-white flex justify-between items-start gap-3 flex-wrap">
    <div><h3 className="font-bold text-lg">{title}</h3><p className="text-sm text-white/70">{sub}</p></div>
    <div className="flex gap-2 print:hidden">{children}</div>
  </div>
);

const Tools = ({ onCsv }) => (
  <>
    <Button size="sm" variant="secondary" className="h-8 gap-1" onClick={onCsv}><Download className="w-3.5 h-3.5" />CSV</Button>
    <Button size="sm" variant="secondary" className="h-8 gap-1" onClick={() => window.print()}><Printer className="w-3.5 h-3.5" />Print</Button>
  </>
);

/* ------------------------------------------------------------ P&L */
export function ProfitLossStatement({ from: from0 = yearStart(), to: to0 = today(), compact = false }) {
  const [from, setFrom] = useState(from0);
  const [to, setTo] = useState(to0);
  const [pl, loading] = useReport(() => ledgerApi.profitLoss(from, to), [from, to]);
  const [byMonth, setByMonth] = useState(false);
  const months = pl?.months || [];

  const csv = () => {
    const rows = [];
    const add = (section, list) => list.forEach((r) => rows.push([section, r.name, ...months.map((m) => r.byMonth[m]), r.total]));
    add('Income', pl.income); add('Cost of goods sold', pl.cogs); add('Expenses', pl.expenses);
    rows.push(['', 'Net income', ...months.map((m) => pl.netByMonth[m]), pl.netIncome]);
    downloadCSV(`profit-loss-${from}-to-${to}.csv`, ['Section', 'Account', ...months, 'Total'], rows);
  };

  const section = (title, list, total) => (
    <>
      <Line label={title} value="" bold />
      {list.map((r) => <Line key={r.accountId} label={r.name} value={acct(r.total)} indent={1} />)}
      <Line label={`Total ${title.toLowerCase()}`} value={acct(total)} bold total />
    </>
  );

  return (
    <div className="space-y-3">
      {!compact && (
        <div className="flex items-end gap-3 flex-wrap print:hidden">
          <DateField label="From" value={from} onChange={setFrom} testId="pl-from" />
          <DateField label="To" value={to} onChange={setTo} testId="pl-to" />
          <Button variant="outline" size="sm" className="h-9" onClick={() => setByMonth((v) => !v)}>{byMonth ? 'Totals only' : 'By month'}</Button>
        </div>
      )}
      <Card className="border-slate-200 overflow-hidden" data-testid="pl-statement">
        <Header title="Profit & Loss" sub={`${from} to ${to}${loading ? ' · updating…' : ''}`}>{pl && <Tools onCsv={csv} />}</Header>
        {!pl ? <Empty>Loading…</Empty> : byMonth ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase"><tr><th className="px-4 py-2 text-left">Account</th>{months.map((m) => <th key={m} className="px-3 py-2 text-right">{monthName(m)}</th>)}<th className="px-4 py-2 text-right">Total</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {[['Income', pl.income], ['Cost of goods sold', pl.cogs], ['Expenses', pl.expenses]].map(([t, list]) => (
                  <React.Fragment key={t}>
                    {list.length ? <tr><td colSpan={months.length + 2} className="px-4 pt-3 font-bold text-slate-900">{t}</td></tr> : null}
                    {list.map((r) => <tr key={r.accountId}><td className="px-4 py-1.5 pl-8 text-slate-700">{r.name}</td>{months.map((m) => <td key={m} className="px-3 py-1.5 text-right">{acct(r.byMonth[m])}</td>)}<td className="px-4 py-1.5 text-right font-semibold">{acct(r.total)}</td></tr>)}
                  </React.Fragment>
                ))}
                <tr className="border-t-2 border-slate-900 font-extrabold"><td className="px-4 py-2">Net income</td>{months.map((m) => <td key={m} className="px-3 py-2 text-right">{acct(pl.netByMonth[m])}</td>)}<td className="px-4 py-2 text-right">{acct(pl.netIncome)}</td></tr>
              </tbody>
            </table>
          </div>
        ) : (
          <>
            {section('Income', pl.income, pl.totalIncome)}
            {pl.cogs.length ? section('Cost of goods sold', pl.cogs, pl.totalCogs) : null}
            {pl.cogs.length ? <Line label="Gross profit" value={acct(pl.grossProfit)} bold total /> : null}
            {section('Expenses', pl.expenses, pl.totalExpense)}
            <div className={`flex justify-between py-4 px-4 border-t-4 border-slate-900 ${pl.netIncome >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
              <span className="font-extrabold text-slate-900">NET INCOME</span>
              <span className={`font-extrabold text-xl tabular-nums ${pl.netIncome >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{money(pl.netIncome)}</span>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------ Balance sheet */
export function BalanceSheetStatement({ asOf: asOf0 = today(), compact = false }) {
  const [asOf, setAsOf] = useState(asOf0);
  const [bs, loading] = useReport(() => ledgerApi.balanceSheet(asOf), [asOf]);

  const csv = () => {
    const rows = [];
    const add = (s, list) => list.forEach((r) => rows.push([s, r.name, r.amount, r.prior]));
    add('Current assets', bs.currentAssets); add('Fixed assets', bs.fixedAssets); add('Other assets', bs.otherAssets);
    add('Current liabilities', bs.currentLiabilities); add('Long-term liabilities', bs.longTermLiabilities); add('Equity', bs.equity);
    rows.push(['Equity', 'Net income (all periods)', bs.netIncome, bs.priorNetIncome]);
    downloadCSV(`balance-sheet-${asOf}.csv`, ['Section', 'Account', asOf, bs.priorAsOf], rows);
  };

  const group = (title, list) => (list.length ? (
    <>
      <Line label={title} value="" indent={1} bold />
      {list.map((r) => <Line key={r.accountId} label={r.name} value={acct(r.amount)} prior={r.prior} indent={2} />)}
    </>
  ) : null);

  return (
    <div className="space-y-3">
      {!compact && <div className="print:hidden"><DateField label="As of" value={asOf} onChange={setAsOf} testId="bs-asof" /></div>}
      <Card className="border-slate-200 overflow-hidden" data-testid="bs-statement">
        <Header title="Balance Sheet" sub={`As of ${asOf}${loading ? ' · updating…' : ''}`}>{bs && <Tools onCsv={csv} />}</Header>
        {!bs ? <Empty>Loading…</Empty> : (
          <>
            <div className="flex justify-end gap-6 px-4 py-2 text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
              <span className="w-28 text-right">{bs.priorAsOf}</span><span className="w-28 text-right">{bs.asOf}</span>
            </div>
            <Line label="ASSETS" value="" bold />
            {group('Current assets', bs.currentAssets)}
            {group('Fixed assets', bs.fixedAssets)}
            {group('Other assets', bs.otherAssets)}
            <Line label="Total assets" value={acct(bs.totalAssets)} prior={bs.priorTotalAssets} bold total />
            <Line label="LIABILITIES" value="" bold />
            {group('Current liabilities', bs.currentLiabilities)}
            {group('Long-term liabilities', bs.longTermLiabilities)}
            <Line label="Total liabilities" value={acct(bs.totalLiabilities)} prior={bs.priorTotalLiabilities} bold total />
            <Line label="EQUITY" value="" bold />
            {bs.equity.map((r) => <Line key={r.accountId} label={r.name} value={acct(r.amount)} prior={r.prior} indent={2} />)}
            <Line label="Net income (all periods to date)" value={acct(bs.netIncome)} prior={bs.priorNetIncome} indent={2} />
            <Line label="Total equity" value={acct(bs.totalEquity)} prior={bs.priorTotalEquity} bold total />
            <div className="flex justify-between items-center py-4 px-4 border-t-4 border-slate-900">
              <span className="font-extrabold text-slate-900">Total liabilities &amp; equity</span>
              <span className="flex items-center gap-3">
                <Badge className={`border-0 ${bs.balanced ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{bs.balanced ? 'Balanced' : 'Out of balance'}</Badge>
                <span className="font-extrabold text-xl tabular-nums">{money(bs.totalLiabilitiesAndEquity)}</span>
              </span>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------ Cash flow */
function CashFlowStatement() {
  const [from, setFrom] = useState(yearStart());
  const [to, setTo] = useState(today());
  const [cf, loading] = useReport(() => ledgerApi.cashFlow(from, to), [from, to]);
  const csv = () => downloadCSV(`cash-flow-${from}-to-${to}.csv`, ['Activity', 'Line', 'Amount'], [
    ...cf.operating.map((l) => ['Operating', l.label, l.amount]), ...cf.investing.map((l) => ['Investing', l.label, l.amount]),
    ...cf.financing.map((l) => ['Financing', l.label, l.amount]), ['', 'Beginning cash', cf.beginningCash], ['', 'Ending cash', cf.endingCash]]);
  const part = (title, list, net) => (
    <>
      <Line label={title} value="" bold />
      {list.length ? list.map((l) => <Line key={l.label} label={l.label} value={acct(l.amount)} indent={1} />) : <Line label="No activity" value="—" indent={1} />}
      <Line label={`Net cash from ${title.toLowerCase()}`} value={acct(net)} bold total />
    </>
  );
  return (
    <div className="space-y-3">
      <div className="flex items-end gap-3 print:hidden">
        <DateField label="From" value={from} onChange={setFrom} />
        <DateField label="To" value={to} onChange={setTo} />
      </div>
      <Card className="border-slate-200 overflow-hidden" data-testid="cf-statement">
        <Header title="Cash Flow" sub={`${from} to ${to}${loading ? ' · updating…' : ''}`}>{cf && <Tools onCsv={csv} />}</Header>
        {!cf ? <Empty>Loading…</Empty> : (
          <>
            <Line label="Cash at the start" value={money(cf.beginningCash)} bold />
            {part('Operating activities', cf.operating, cf.netOperating)}
            {part('Investing activities', cf.investing, cf.netInvesting)}
            {part('Financing activities', cf.financing, cf.netFinancing)}
            <Line label="Net change in cash" value={acct(cf.netChange)} bold total />
            <div className="flex justify-between py-4 px-4 border-t-4 border-slate-900">
              <span className="font-extrabold">Cash at the end</span>
              <span className="font-extrabold text-xl tabular-nums">{money(cf.endingCash)}</span>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------ Trial balance */
function TrialBalanceStatement() {
  const [to, setTo] = useState(today());
  const [tb, loading] = useReport(() => ledgerApi.trialBalance('', to), [to]);
  const csv = () => downloadCSV(`trial-balance-${to}.csv`, ['Code', 'Account', 'Type', 'Debit', 'Credit'],
    tb.rows.map((r) => [r.code, r.name, r.type, r.debitBal || '', r.creditBal || '']));
  return (
    <div className="space-y-3">
      <div className="print:hidden"><DateField label="As of" value={to} onChange={setTo} /></div>
      <Card className="border-slate-200 overflow-hidden" data-testid="tb-statement">
        <Header title="Trial Balance" sub={`As of ${to}${loading ? ' · updating…' : ''}`}>{tb && <Tools onCsv={csv} />}</Header>
        {!tb ? <Empty>Loading…</Empty> : (
          <table className="w-full text-sm tabular-nums">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase"><tr><th className="px-4 py-2 text-left">Account</th><th className="px-4 py-2 text-left">Type</th><th className="px-4 py-2 text-right">Debit</th><th className="px-4 py-2 text-right">Credit</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {tb.rows.map((r) => (
                <tr key={r.accountId}><td className="px-4 py-2 text-slate-800">{r.code ? `${r.code} · ` : ''}{r.name}</td><td className="px-4 py-2 text-slate-500">{r.type}</td>
                  <td className="px-4 py-2 text-right">{r.debitBal ? money(r.debitBal) : ''}</td><td className="px-4 py-2 text-right">{r.creditBal ? money(r.creditBal) : ''}</td></tr>
              ))}
              <tr className="border-t-2 border-slate-900 font-extrabold"><td className="px-4 py-3" colSpan={2}>
                Totals <Badge className={`ml-2 border-0 ${tb.balanced ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>{tb.balanced ? 'In balance' : 'Out of balance'}</Badge>
              </td><td className="px-4 py-3 text-right">{money(tb.totalDebit)}</td><td className="px-4 py-3 text-right">{money(tb.totalCredit)}</td></tr>
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

export default function Statements() {
  const [key, setKey] = useState(0);
  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-statements">
      <PageTitle title="Financial Statements" subtitle="Built from every invoice, payment, expense, bill and journal entry — they update as records change.">
        <Button variant="outline" className="gap-1.5 print:hidden" onClick={() => setKey((k) => k + 1)}><RefreshCw className="w-4 h-4" />Refresh</Button>
      </PageTitle>
      <Tabs defaultValue="pl" key={key}>
        <TabsList className="print:hidden">
          <TabsTrigger value="pl">Profit &amp; Loss</TabsTrigger>
          <TabsTrigger value="bs">Balance Sheet</TabsTrigger>
          <TabsTrigger value="cf">Cash Flow</TabsTrigger>
          <TabsTrigger value="tb">Trial Balance</TabsTrigger>
        </TabsList>
        <TabsContent value="pl" className="mt-5"><ProfitLossStatement /></TabsContent>
        <TabsContent value="bs" className="mt-5"><BalanceSheetStatement /></TabsContent>
        <TabsContent value="cf" className="mt-5"><CashFlowStatement /></TabsContent>
        <TabsContent value="tb" className="mt-5"><TrialBalanceStatement /></TabsContent>
      </Tabs>
    </div>
  );
}
